// @ts-check
import { GetSearchKeysByView } from 'components/Search/Filters/keys';
import { searchOrigin } from 'components/Search/Results/constants';
import { withLabelledSortBy } from './sortByLabel';
import { buildReviewRows } from './filterReview';

// Stage two bundles sortBy in with the filters, and no key group defines it.
const sortByMeta = {
  key: 'sortBy',
  description: 'Sort By',
  getTitle: () => 'Sort By',
};

/**
 * Every dropped key is one the org panels do not expose, so only the
 * main-search bag can name them.
 */
const ignoredFilterMeta = () => ({
  ...(GetSearchKeysByView(searchOrigin.researcherSearch) || {}),
  sortBy: sortByMeta,
});

/**
 * @param {Record<string, any>} meta
 * @param {string} key
 * @returns {string}
 */
const fieldName = (meta, key) => {
  const definition = meta[key];
  if (!definition) return key;
  if (typeof definition.getDescription === 'function') {
    return definition.getDescription({});
  }
  return definition.description || definition.title || key;
};

/**
 * Readable values out of the nested shapes the review tokeniser skips — an
 * organisationCategory is `[{ value, children }]`, several levels deep.
 *
 * @param {any} value
 * @returns {string[]}
 */
const flattenValues = value => {
  if (value == null || value === false) return [];
  if (Array.isArray(value)) return value.flatMap(flattenValues);
  if (typeof value === 'object') {
    const own = value.value ?? value.label ?? value.name ?? value.title;
    const nested = flattenValues(value.children);
    return [...(typeof own === 'string' ? [own] : []), ...nested];
  }
  return value === true ? [] : [String(value)];
};

/**
 * Rows for the filters an organisation profile will not apply, shaped exactly
 * like the applied ones beside them.
 *
 * The review tokeniser drops sub-objects by design, so keys it cannot express
 * get a row built by hand: a dropped filter that renders nothing leaves the
 * user with no sign it was dropped, which is the whole problem.
 *
 * @param {Record<string, any> | null | undefined} droppedFilters
 * @returns {{ key: string, field: string, values: any[] }[]}
 */
export function buildIgnoredFilterRows(droppedFilters) {
  if (!droppedFilters || Object.keys(droppedFilters).length === 0) return [];

  const meta = ignoredFilterMeta();
  const rows = buildReviewRows(droppedFilters, meta).sections.flatMap(
    section => section.rows
  );
  const rendered = new Set(rows.map(row => row.key));

  const unrendered = Object.entries(droppedFilters)
    .filter(([key]) => !rendered.has(key))
    .map(([key, value]) => ({
      key,
      field: fieldName(meta, key),
      values: flattenValues(value),
    }));

  return withLabelledSortBy([...rows, ...unrendered]);
}
